import { useState } from "react";
import { Modal, Card, Fade, Backdrop, Icon, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";

// Services
import comprasService from "services/comprasService";

function CompraConfirmacionModal({ open, onClose, planData }) {
  const [procesando, setProcesando] = useState(false);

  /**
   * Maneja el proceso de compra
   * 1. Crea el registro de compra en el backend (estado: PENDIENTE)
   * 2. En el futuro, redirigirá a PSE para el pago
   * 3. Las licencias se asignarán cuando el superadmin apruebe el pago
   */
  const handleContinuar = async () => {
    setProcesando(true);
    
    try {
      console.log('🛒 Iniciando proceso de compra para:', planData);
      
      // Crear la compra en el backend
      const resultado = await comprasService.crearCompra({
        paquete_id: planData?.id,
        cant_paquetes: 1, // Por ahora siempre es 1 paquete
      });

      console.log('✅ Compra registrada:', resultado);
      
      // Mostrar mensaje de éxito
      alert(
        `¡Compra registrada exitosamente!\n\n` +
        `Paquete: ${planData?.nombre}\n` +
        `Licencias: ${planData?.tests}\n` +
        `Monto: $${planData?.precio?.toLocaleString('es-CO')}\n` +
        `Estado: PENDIENTE\n\n` +
        `Las licencias se activarán una vez el pago sea verificado por el administrador.`
      );
      
      // Cerrar modal
      onClose();
      
    } catch (error) {
      console.error('❌ Error al procesar compra:', error);
      alert(`Error al procesar la compra:\n${error.message || 'Error desconocido'}`);
    } finally {
      setProcesando(false);
    }
  };

  // Determinar el color del gradiente según el plan
  const getGradientColor = () => {
    switch(planData?.color) {
      case "info":
        return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
      case "success":
        return "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)";
      case "warning":
        return "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)";
      default:
        return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
    }
  };

  return (
    <Modal
      open={open}
      onClose={procesando ? undefined : onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: "rgba(0, 0, 0, 0.75)" }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Fade in={open}>
        <Card
          sx={{
            width: { xs: "85%", sm: "450px", md: "500px" },
            maxWidth: "600px",
            maxHeight: "90vh",
            outline: "none",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            borderRadius: "16px",
            overflow: "auto"
          }}
        >
          {/* Header con gradiente */}
          <MDBox
            p={4}
            sx={{
              background: getGradientColor(),
              position: "relative"
            }}
          >
            <MDBox display="flex" justifyContent="center" mb={2}>
              <MDBox
                sx={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)"
                }}
              >
                <Icon
                  sx={{
                    fontSize: "48px !important",
                    color: "white"
                  }}
                >
                  {procesando ? "hourglass_empty" : "check_circle"}
                </Icon>
              </MDBox>
            </MDBox>
            <MDTypography
              variant="h4"
              color="white"
              textAlign="center"
              fontWeight="bold"
            >
              {procesando ? "Procesando Compra..." : "¡Selección Confirmada!"}
            </MDTypography>
            <MDTypography
              variant="body2"
              color="white"
              textAlign="center"
              mt={1}
              sx={{ opacity: 0.9 }}
            >
              {procesando ? "Por favor espera" : "Revisa los detalles de tu compra"}
            </MDTypography>
          </MDBox>

          {/* Cuerpo del modal */}
          <MDBox p={4} sx={{ backgroundColor: "#ffffff" }}>
            {/* Tarjeta de información */}
            <Card
              sx={{
                p: 3,
                backgroundColor: "#f8fafc",
                boxShadow: "none",
                border: "1px solid #e2e8f0",
                borderRadius: "12px"
              }}
            >
              {/* Plan */}
              <MDBox mb={3}>
                <MDTypography 
                  variant="caption" 
                  color="text" 
                  fontWeight="medium"
                  sx={{ 
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontSize: "0.75rem"
                  }}
                >
                  Plan seleccionado
                </MDTypography>
                <MDTypography 
                  variant="h4" 
                  fontWeight="bold" 
                  color="dark"
                  mt={0.5}
                >
                  {planData?.nombre}
                </MDTypography>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {/* Tests */}
              <MDBox 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
                mb={2}
              >
                <MDBox display="flex" alignItems="center">
                  <Icon sx={{ mr: 1, color: "#64748b" }}>assignment</Icon>
                  <MDTypography variant="body1" color="text" fontWeight="medium">
                    Licencias a Agregar
                  </MDTypography>
                </MDBox>
                <MDTypography 
                  variant="h5" 
                  fontWeight="bold" 
                  color="dark"
                >
                  {planData?.tests}
                </MDTypography>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {/* Precio */}
              <MDBox 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
              >
                <MDBox display="flex" alignItems="center">
                  <Icon sx={{ mr: 1, color: "#64748b" }}>payments</Icon>
                  <MDTypography variant="body1" color="text" fontWeight="medium">
                    Total a pagar
                  </MDTypography>
                </MDBox>
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  color={planData?.color || "info"}
                >
                  ${planData?.precio?.toLocaleString('es-CO')}
                </MDTypography>
              </MDBox>
            </Card>

            {/* Información de redirección */}
            <MDBox 
              mt={3} 
              p={2.5}
              sx={{ 
                backgroundColor: procesando ? "#fff7ed" : "#eff6ff",
                borderRadius: "8px",
                border: procesando ? "1px solid #fed7aa" : "1px solid #bfdbfe"
              }}
            >
              <MDBox display="flex" alignItems="flex-start">
                <Icon sx={{ color: procesando ? "#f97316" : "#3b82f6", mr: 1.5, mt: 0.3 }}>
                  {procesando ? "hourglass_empty" : "info"}
                </Icon>
                <MDBox>
                  <MDTypography variant="body2" color="text" fontWeight="medium">
                    {procesando ? (
                      <>Registrando tu compra en el sistema. Por favor espera...</>
                    ) : (
                      <>
                        Al confirmar, se creará un <strong>registro de compra pendiente</strong>. 
                        Las licencias se activarán una vez el pago sea verificado.
                      </>
                    )}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            {/* Mensaje temporal sobre el flujo actual */}
            {!procesando && (
              <MDBox 
                mt={2} 
                p={2}
                sx={{ 
                  backgroundColor: "#fef3c7",
                  borderRadius: "8px",
                  border: "1px solid #fde68a"
                }}
              >
                <MDBox display="flex" alignItems="flex-start">
                  <Icon sx={{ color: "#f59e0b", mr: 1.5, mt: 0.3, fontSize: "20px" }}>
                    construction
                  </Icon>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      <strong>Flujo actual:</strong> La compra quedará registrada como PENDIENTE. 
                      Un administrador deberá verificar el pago y aprobar la transacción para 
                      activar las licencias. La integración con PSE estará disponible próximamente.
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            )}

            {/* Botones de acción */}
            <MDBox mt={3} display="flex" gap={2}>
              <MDButton
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={onClose}
                disabled={procesando}
                sx={{
                  borderWidth: "2px",
                  "&:hover": {
                    borderWidth: "2px"
                  }
                }}
              >
                Cancelar
              </MDButton>
              <MDButton
                variant="gradient"
                color={planData?.color || "info"}
                fullWidth
                onClick={handleContinuar}
                disabled={procesando}
                sx={{
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                }}
              >
                {procesando ? (
                  <>
                    <Icon sx={{ mr: 1 }}>hourglass_empty</Icon>
                    Procesando...
                  </>
                ) : (
                  "Continuar"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
}

CompraConfirmacionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  planData: PropTypes.shape({
    id: PropTypes.number,
    nombre: PropTypes.string,
    tests: PropTypes.number,
    precio: PropTypes.number,
    color: PropTypes.string,
    icon: PropTypes.string
  })
};

export default CompraConfirmacionModal;